import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { NativeSelect, NativeSelectOption } from "@/components/ui/native-select";
import { ToggleGroup, ToggleGroupCount, ToggleGroupItem } from "@/components/ui/toggle-group";
import {
  CeldaVigencia,
  DataTable,
  Icon,
  KpiCard,
  KpiGrid,
  TextoTruncado,
  VigenciaModal,
} from "@/components/ds";
import { apiRecursoDocumentosAdapter as adapterDocs } from "@/lib/recurso-documentos-adapter";
import type { RecursoDocumento } from "@/types/recurso-documento";
import {
  FormDrawer,
  FormDrawerContent,
  FormDrawerDescription,
  FormDrawerFooter,
  FormDrawerHeader,
  FormDrawerTitle,
  formDrawerBodyClass,
} from "@/components/ds/FormDrawer";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Tip } from "@/components/ds/Tip";
import { AlertCircle, Loader2, Plus, Search } from "@/components/ds/icons";
import { PageHeader } from "@/components/ui/PageHeader";
import { toast } from "sonner";
import { api, ApiError } from "@/lib/api";
import { cn } from "@/lib/utils";
import { badgeVencimiento, estadoVencimiento } from "@/lib/vencimiento";
import { CATEGORIAS_ELEMENTO, type CategoriaElemento, type Elemento } from "@/types/elemento";
import type { DriveStep } from "driver.js";
import { useGuide } from "@/hooks/useGuide";
import { useRegisterPageGuide } from "@/contexts/ActiveGuideContext";
import { useGuideAutostart } from "@/hooks/useGuideAutostart";
import { usePendingGuide } from "@/hooks/usePendingGuide";
import { useSetupStatusContext } from "@/contexts/SetupStatusContext";

// ─── Vocabulario ─────────────────────────────────────────────────────────────

/**
 * Interruptor único de la CATEGORÍA del elemento.
 *
 * Está en `false` porque el stakeholder no la quiere por ahora. Se OCULTA, no
 * se borra: controlar un catálogo de equipamiento grande sin agruparlo no se
 * sostiene mucho tiempo, así que es cuestión de tiempo que vuelva.
 *
 * Lo que este flag apaga es la UI —la columna de la tabla, el campo del
 * formulario y el filtro del buscador—. NADA del backend cambia: la columna
 * `elementos.categoria` sigue existiendo (nullable), el enum
 * `App\Enums\CategoriaElemento` sigue validando, el catálogo por defecto se
 * sigue sembrando categorizado y `fc:backfill-categorias-elementos` sigue
 * sirviendo. Re-prenderlo = este booleano en `true`.
 *
 * Va tipado `: boolean` explícito y no como literal `false` a propósito: con el
 * literal, TypeScript da por imposibles las ramas del `true` que el archivo
 * conserva justamente para cuando se vuelva a prender.
 *
 * CONSECUENCIA a mirar antes de prenderlo: mientras esté oculto, los elementos
 * que cree la empresa nacen SIN categoría (el formulario no la pide). El
 * backfill sólo reconoce los nombres del catálogo por defecto, así que lo que
 * se haya creado a mano en el medio va a quedar sin clasificar y habrá que
 * repasarlo desde el panel.
 */
const CATEGORIA_VISIBLE: boolean = false;

/**
 * Ancho mínimo de la grilla: la suma de las columnas más los 28px del `px-3.5`
 * de la fila. Depende del flag porque una columna de menos son 116px menos —
 * dejar el número fijo abriría un hueco muerto a la derecha.
 */
const MIN_WIDTH_TABLA = CATEGORIA_VISIBLE ? 980 : 864;

/**
 * Las cuatro vistas del listado. `papelera` NO es un estado más del elemento:
 * es un conjunto aparte que se le pide al servidor con otro filtro, por eso
 * viaja como vista y no como un valor del filtro de estado.
 *
 * `todos` es el catálogo vivo completo —activos + inactivos, sin papelera—: es
 * el mismo conjunto del que se pliegan las cuatro tarjetas de KPI, así que sin
 * esta pestaña no había forma de ver en la tabla las filas que esas tarjetas
 * estaban contando.
 */
type Vista = "todos" | "activos" | "inactivos" | "papelera";

function plural(n: number, singular: string, plural: string): string {
  return `${n} ${n === 1 ? singular : plural}`;
}

/**
 * Estado del certificado de calidad de UN elemento.
 *
 * Es la ÚNICA función que interpreta esos campos: las columnas la usan para
 * pintar la fila y las tarjetas de KPI la usan para contar cuántos elementos
 * caen en cada caso. Si el criterio de "al día" se escribiera dos veces, la
 * tarjeta y la columna terminarían discrepando — misma restricción que el
 * listado de Vehículos.
 *
 * `certificado_cargado` es un gate independiente de `certificado_vencimiento`
 * (ver `types/elemento.ts`): sin él, "sin certificado" y "certificado cargado
 * sin fecha" serían indistinguibles, porque los dos dan vencimiento `null`.
 */
interface EstadoCertificado {
  cargado: boolean;
  vencido: boolean;
  porVencer: boolean;
  /** Cargado y no vencido — lo que cuenta "Con certificado al día". Una fecha
   * que se acerca NO descalifica: el certificado todavía sirve. */
  alDia: boolean;
  estado: ReturnType<typeof estadoVencimiento>;
}

function estadoCertificado(el: Elemento): EstadoCertificado {
  if (!el.certificado_cargado) {
    return {
      cargado: false,
      vencido: false,
      porVencer: false,
      alDia: false,
      estado: "sin_vencimiento",
    };
  }

  const estado = estadoVencimiento(el.certificado_vencimiento);

  return {
    cargado: true,
    vencido: estado === "vencido",
    porVencer: estado === "por_vencer",
    alDia: estado !== "vencido",
    estado,
  };
}

// ─── Celdas ──────────────────────────────────────────────────────────────────

/** Nombre del elemento con su ciclo de renovación debajo, como el prototipo. */
function CeldaElemento({ el }: { el: Elemento }) {
  return (
    // `pr-2` porque la grilla de DataTable no tiene gap entre columnas: sin ese
    // respiro el texto recortado queda pegado a la columna siguiente.
    <div className="flex min-w-0 flex-col pr-2">
      <TextoTruncado className="block truncate text-[13px] font-medium">
        {el.nombre}
      </TextoTruncado>
      <span className="text-soft truncate text-[11px]">{el.ciclo_renovacion}</span>
    </div>
  );
}

/**
 * Badge del certificado + las acciones que corresponden a su estado.
 *
 * Tres estados, como el prototipo: "Subiendo…" con spinner mientras viaja el
 * archivo, un botón "Subir" cuando falta, y el badge de vigencia con
 * descargar/reemplazar cuando ya está.
 *
 * Descargar y reemplazar son DOS botones y no uno de "ver documento": son
 * acciones distintas y el prototipo las separa. Las tres van contra
 * `apiRecursoDocumentosAdapter`, el mismo que usa el panel de la ficha — no hay
 * una segunda implementación de la subida.
 */
function CeldaCertificado({
  el,
  subiendo,
  ocupada,
  onSubir,
  onDescargar,
}: {
  el: Elemento;
  subiendo: boolean;
  ocupada: boolean;
  onSubir: (el: Elemento) => void;
  onDescargar: (el: Elemento) => void;
}) {
  const cert = estadoCertificado(el);

  if (subiendo) {
    return (
      <span className="text-primary-hover flex min-w-0 items-center gap-1.5 pr-2 text-[11.5px]">
        <Loader2 className="h-[15px] w-[15px] shrink-0 animate-spin" />
        Subiendo…
      </span>
    );
  }

  if (!cert.cargado) {
    return (
      <div className="flex min-w-0 items-center pr-2">
        <Button
          variant="outline"
          size="xs"
          className="border-primary-border bg-primary-50 text-primary-hover hover:bg-primary-100"
          onClick={() => onSubir(el)}
        >
          <Icon name="upload_file" size={15} />
          Subir
        </Button>
      </div>
    );
  }

  // Un certificado cargado SIN fecha de vencimiento se lee "Vigente", no
  // "Sin vencimiento": esta columna informa si el certificado sirve, y sirve.
  // Que no venza ya lo dice la columna de al lado ("No vence" + "sin
  // recordatorios"); repetirlo acá gasta la columna en decir dos veces lo
  // mismo y deja al elemento con el badge gris de los estados incompletos.
  const { label, tone } =
    el.certificado_vencimiento === null
      ? { label: "Vigente", tone: "success" as const }
      : badgeVencimiento(cert.estado);

  return (
    <div className="flex min-w-0 items-center gap-1.5 pr-2">
      {/* `shrink` pisa el `shrink-0` que trae badgeVariants: sin eso el badge
          conserva su ancho natural y se desborda sobre la columna vecina. */}
      <Badge variant={tone} dot className="min-w-0 shrink">
        {label}
      </Badge>
      {/* Descargar y reemplazar son DOS acciones distintas y por eso son dos
          botones, como en el prototipo. Con un solo icono "ver documento" no
          había forma de bajar el archivo sin pasar por un diálogo. */}
      <div className="flex shrink-0 items-center gap-0.5">
        <Tip texto="Descargar certificado">
          <Button
            variant="ghost"
            size="icon-sm"
            disabled={ocupada}
            aria-label={`Descargar el certificado de ${el.nombre}`}
            onClick={() => onDescargar(el)}
          >
            {ocupada ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <Icon name="download" size={16} />
            )}
          </Button>
        </Tip>
        <Tip texto="Reemplazar certificado">
          <Button
            variant="ghost"
            size="icon-sm"
            className="hover:text-primary-hover"
            aria-label={`Reemplazar el certificado de ${el.nombre}`}
            onClick={() => onSubir(el)}
          >
            <Icon name="upload_file" size={16} />
          </Button>
        </Tip>
      </div>
    </div>
  );
}

/** Fecha de vencimiento del certificado + cuántos avisos va a disparar. */
function CeldaVencimiento({
  el,
  ocupada,
  onAbrir,
}: {
  el: Elemento;
  ocupada: boolean;
  onAbrir: (el: Elemento) => void;
}) {
  const cert = estadoCertificado(el);

  // Sin certificado la celda NO va vacía: anticipa que la fecha se calcula sola
  // al subir. Un guion dejaba la columna muda justo en las filas donde falta
  // algo. Con certificado, la API no distingue "no vence" de "todavía sin
  // decidir" —guardar "no vence" es guardar `fecha_vencimiento: null`—, así que
  // los dos llegan idénticos y se muestran "No vence", que es lo que el dato
  // dice. Llamarlo "Definir fecha" acusaría de incompleto a un certificado que
  // la empresa marcó permanente a propósito, y el modal sigue a un clic.
  return (
    <CeldaVigencia
      label={`el certificado de ${el.nombre}`}
      sinArchivo={!cert.cargado}
      fechaVencimiento={el.certificado_vencimiento}
      avisos={el.certificado_avisos}
      estado={cert.vencido ? "vencido" : cert.porVencer ? "por_vencer" : undefined}
      onEditar={cert.cargado ? () => onAbrir(el) : undefined}
      ocupada={ocupada}
      className="pr-2"
    />
  );
}

function EstadoBadge({ el }: { el: Elemento }) {
  if (el.eliminado_at) {
    return (
      <Badge variant="destructive" className="min-w-0 shrink gap-1">
        <Icon name="delete" size={13} />
        En papelera
      </Badge>
    );
  }
  return (
    <Badge variant={el.activo ? "success" : "neutral"} className="min-w-0 shrink">
      {el.activo ? "Activo" : "Inactivo"}
    </Badge>
  );
}

// ─── Componente principal ─────────────────────────────────────────────────────

export function EPP() {
  // `inventario` = activos + inactivos, sin papelera. Es el conjunto del que se
  // pliegan las cuatro tarjetas: un elemento inactivo con el certificado
  // vencido SIGUE contando, porque sale de las entregas nuevas pero su papel
  // hay que arreglarlo antes de reactivarlo. La papelera es otro conjunto y no
  // entra en ningún denominador.
  const [inventario, setInventario] = useState<Elemento[]>([]);
  const [papelera, setPapelera] = useState<Elemento[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [search, setSearch] = useState("");
  const [vista, setVista] = useState<Vista>("activos");
  const [createOpen, setCreateOpen] = useState(false);
  const [editing, setEditing] = useState<Elemento | null>(null);

  // ── Certificado en la fila ────────────────────────────────────────────────
  //
  // El índice de elementos trae el RESUMEN del certificado (si está cargado,
  // cuándo vence, cuántos avisos) pero no el documento en sí. Para actuar sobre
  // él —descargarlo, reemplazarlo, editarle la vigencia— hace falta su id, y
  // pedirlo para las 20 filas de entrada sería traer 20 documentos que casi
  // nadie va a tocar. Se pide el de UNA fila recién cuando se la toca.
  const [tipoCertificadoId, setTipoCertificadoId] = useState<number | null>(null);
  const [subiendo, setSubiendo] = useState<Record<number, boolean>>({});
  const [ocupada, setOcupada] = useState<number | null>(null);
  const [vigencia, setVigencia] = useState<{ elemento: Elemento; documento: RecursoDocumento } | null>(
    null,
  );
  // Un solo input de archivo para toda la tabla: montar uno por fila serían 20
  // inputs ocultos en el DOM para un click que ocurre de a uno.
  const inputArchivoRef = useRef<HTMLInputElement>(null);
  const elementoASubirRef = useRef<Elemento | null>(null);
  // Confirmación de borrado definitivo EN LA FILA (dos pasos), como el
  // prototipo: el primer click arma, el segundo borra.
  const [confirmandoBorrado, setConfirmandoBorrado] = useState<number | null>(null);


  // Desactivar (PUT activo=false — eje vigencia, NO papelera)
  const [aDesactivar, setADesactivar] = useState<Elemento | null>(null);
  const [desactivando, setDesactivando] = useState(false);

  // Mover a papelera (DELETE → soft delete)
  const [aEliminar, setAEliminar] = useState<Elemento | null>(null);
  const [eliminando, setEliminando] = useState(false);

  // Restaurar desde papelera
  const [aRestaurar, setARestaurar] = useState<Elemento | null>(null);
  const [restaurando, setRestaurando] = useState(false);

  // Purgar (hard delete)
  const [purgando, setPurgando] = useState(false);

  // --- Guía piloto de EPP ---
  const { invalidate } = useSetupStatusContext();

  /**
   * guideInDialog: true mientras la guía está activa con el diálogo abierto.
   * Se usa para pasar modal={false} al FormDrawer durante esos pasos,
   * evitando que el focus trap de Radix bloquee los botones Siguiente/Anterior
   * del popover de driver.js (que se renderizan fuera del DOM del diálogo).
   *
   * En el flujo normal (sin guía) el diálogo sigue siendo modal={true} por defecto.
   */
  const [guideInDialog, setGuideInDialog] = useState(false);

  /**
   * Ref para cancelar el rAF de polling del primer campo del diálogo.
   * Se necesita para no tener efectos secundarios si el componente desmonta.
   */
  const dialogPollRafRef = useRef<number>(0);

  /**
   * Fábrica de pasos lazy: se invoca en startGuide() para capturar el estado
   * vivo de setCreateOpen y setGuideInDialog (estables entre renders).
   *
   * Paso 2 — Highlight del botón Nuevo Elemento:
   *   - onHighlightStarted: agrega driver-fc-dialog-step al body → driver z-index
   *     baja a 40/45 (bajo Radix z-50), el botón queda resaltado sin tapar nada.
   *   - onDeselected: remueve driver-fc-dialog-step → driver vuelve a su z default (~10000).
   *   - onNextClick: abre el diálogo, activa modal={false} en el dialog, hace poll
   *     rAF para esperar que monte el primer campo antes de avanzar.
   *
   * Pasos 3–4 — Campos dentro del diálogo:
   *   - driver en z default (~10000), por encima del Radix Dialog (z-50).
   *   - driver-fc-dialog-step NO está en el body → overlay de driver es visible.
   *   - Radix Dialog tiene modal={false} → focus trap desactivado →
   *     botones Next/Prev de driver son clickeables.
   *
   * Paso 5 — Popover de cierre:
   *   - Desactiva modal={false}, el diálogo puede cerrarse normalmente.
   */
  const buildEppGuideSteps = useCallback((): DriveStep[] => [
    // Paso 1 — Popover centrado de apertura (sin elemento anclado)
    {
      popover: {
        title: "¿Por qué personalizar los EPP?",
        description:
          "El sistema incluye algunos elementos de referencia, pero cada empresa usa equipos distintos. Crea los EPP que tu empresa entrega realmente — así las entregas reflejan tu operación y no un catálogo genérico. Creemos el primero.",
      },
    },
    // Paso 2 — Highlight del botón Nuevo Elemento (pre-diálogo)
    // driver-fc-dialog-step body class está activa → driver z-index bajo (40/45) bajo Radix
    {
      element: '[data-tour="epp-crear"]',
      onHighlightStarted: () => {
        document.body.classList.add("driver-fc-dialog-step");
      },
      onDeselected: () => {
        // Removemos la clase antes de los pasos in-dialog para que driver
        // vuelva a su z default (~10000) y pueda resaltar los campos dentro del diálogo.
        document.body.classList.remove("driver-fc-dialog-step");
      },
      popover: {
        title: "Crear un elemento EPP",
        description:
          "Acá agregas un nuevo elemento al catálogo de tu empresa. Haz clic en «Siguiente» para abrir el formulario.",
        onNextClick: (_el, _step, { driver: driverObj }) => {
          // Abre el diálogo y desactiva el focus trap para que driver mantenga
          // el control. El poll rAF espera a que monte el primer campo antes
          // de avanzar, evitando que driver intente resaltar un nodo inexistente.
          setCreateOpen(true);
          setGuideInDialog(true);

          cancelAnimationFrame(dialogPollRafRef.current);
          const deadline = performance.now() + 3000;
          const tick = () => {
            if (document.querySelector('[data-tour="epp-nombre"]')) {
              driverObj.moveNext();
              return;
            }
            if (performance.now() >= deadline) {
              // Fallback: avanzamos igual para no dejar la guía atascada.
              driverObj.moveNext();
              return;
            }
            dialogPollRafRef.current = requestAnimationFrame(tick);
          };
          dialogPollRafRef.current = requestAnimationFrame(tick);
        },
      },
    },
    // Paso 3 — Campo Nombre (dentro del diálogo, primer campo visible)
    {
      element: '[data-tour="epp-nombre"]',
      popover: {
        title: "Nombre del elemento",
        description:
          "Escribe el nombre exacto del EPP que usas en tu empresa, por ejemplo «Casco seguridad clase A» o «Guantes de cuero caña larga». Sé específico — este nombre aparece en las entregas.",
      },
    },
    // Paso 4 — Botón Guardar (dentro del diálogo)
    {
      element: '[data-tour="epp-guardar"]',
      popover: {
        title: "Guardar elemento",
        description:
          "Cuando hayas ingresado el nombre, guarda para agregar el elemento al catálogo.",
        onNextClick: (_el, _step, { driver: driverObj }) => {
          // Al salir de los pasos in-dialog, volvemos al comportamiento modal normal.
          setGuideInDialog(false);
          driverObj.moveNext();
        },
      },
    },
    // Paso 5 — Popover de cierre/confirmación (sin elemento anclado)
    {
      popover: {
        title: "¡Listo!",
        description:
          "Puedes reabrir esta guía cuando quieras haciendo clic en «Ver guía» en el panel de primeros pasos, o en el botón de ayuda de esta página.",
      },
    },
  ], [setCreateOpen, setGuideInDialog]);

  const { startGuide } = useGuide("guide-epp", { buildSteps: buildEppGuideSteps });
  useRegisterPageGuide(startGuide);
  usePendingGuide("guide-epp", startGuide, { anchorSelector: '[data-tour="epp-crear"]' });
  useGuideAutostart("guide-epp", startGuide, { anchorSelector: '[data-tour="epp-crear"]' });

  // Limpieza del rAF de polling al desmontar
  useEffect(() => {
    return () => cancelAnimationFrame(dialogPollRafRef.current);
  }, []);
  // --- fin Guía piloto ---

  // Las dos listas se piden juntas y de una sola vez: las pestañas muestran su
  // conteo antes de que se las toque, y las tarjetas necesitan el inventario
  // completo. Cambiar de pestaña no vuelve a pegarle al servidor.
  const cargar = useCallback(async () => {
    setIsLoading(true);
    setLoadError(null);
    try {
      const [inv, pap] = await Promise.all([
        api.get<{ data: Elemento[] }>("/elementos?all=1&include_inactivos=1"),
        api.get<{ data: Elemento[] }>("/elementos?all=1&en_papelera=1"),
      ]);
      setInventario(inv.data);
      setPapelera(pap.data);
    } catch {
      setLoadError("No se pudo cargar la lista de elementos.");
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    cargar();
  }, [cargar]);

  // El ámbito `elemento` tiene EXACTAMENTE un tipo en el catálogo cerrado (el
  // certificado de calidad), así que se resuelve una vez para toda la tabla en
  // vez de por fila.
  useEffect(() => {
    let vivo = true;
    adapterDocs
      .listTipos("elemento")
      .then((tipos) => {
        if (vivo) setTipoCertificadoId(tipos[0]?.id ?? null);
      })
      .catch(() => {
        /* Sin tipo no se puede subir; el botón queda deshabilitado y el resto
           de la tabla sigue funcionando. */
      });
    return () => {
      vivo = false;
    };
  }, []);

  /** Trae el certificado de UN elemento. `null` si todavía no subió ninguno. */
  const certificadoDe = useCallback(async (el: Elemento): Promise<RecursoDocumento | null> => {
    const docs = await adapterDocs.listDocumentos("elemento", el.id);
    return docs[0] ?? null;
  }, []);

  const descargarCertificado = async (el: Elemento) => {
    setOcupada(el.id);
    try {
      const doc = await certificadoDe(el);
      if (!doc) {
        toast.error("Este elemento todavía no tiene certificado.");
        return;
      }
      await adapterDocs.download("elemento", el.id, doc);
    } catch {
      toast.error("No se pudo descargar el certificado.");
    } finally {
      setOcupada(null);
    }
  };

  const abrirVigencia = async (el: Elemento) => {
    setOcupada(el.id);
    try {
      const doc = await certificadoDe(el);
      if (!doc) {
        toast.error("Sube el certificado antes de definir su vencimiento.");
        return;
      }
      setVigencia({ elemento: el, documento: doc });
    } catch {
      toast.error("No se pudo abrir la vigencia del certificado.");
    } finally {
      setOcupada(null);
    }
  };

  /** Abre el selector de archivos. Sirve para subir y para reemplazar. */
  const pedirArchivo = (el: Elemento) => {
    if (tipoCertificadoId === null) {
      toast.error("No se pudo cargar el catálogo de documentos. Recarga la página.");
      return;
    }
    elementoASubirRef.current = el;
    inputArchivoRef.current?.click();
  };

  const subirCertificado = async (archivo: File) => {
    const el = elementoASubirRef.current;
    if (!el || tipoCertificadoId === null) return;

    setSubiendo((s) => ({ ...s, [el.id]: true }));
    try {
      // Reemplazar = borrar el anterior y subir el nuevo: el slot es único por
      // (elemento, tipo) y el backend lo rechazaría duplicado.
      const previo = await certificadoDe(el);
      if (previo) await adapterDocs.destroy("elemento", el.id, previo.id);

      const nuevo = await adapterDocs.upload("elemento", el.id, tipoCertificadoId, archivo);

      // Reemplazar un certificado NO es redefinir cuándo vence. `upload` nace
      // sin vigencia, así que si el anterior tenía fecha y avisos hay que
      // devolvérselos: si no, cambiar el archivo apagaría en silencio los
      // recordatorios que la empresa ya había configurado.
      if (previo && (previo.fecha_vencimiento || previo.notificar_dias.length)) {
        await adapterDocs.updateVencimiento("elemento", el.id, nuevo.id, {
          fechaVencimiento: previo.fecha_vencimiento,
          notificarDias: previo.notificar_dias,
        });
      }
      toast.success(
        previo
          ? `Certificado de "${el.nombre}" reemplazado.`
          : `Certificado de "${el.nombre}" subido. Define su vencimiento para que avise antes.`,
      );
      cargar();
    } catch {
      toast.error("No se pudo subir el certificado.");
    } finally {
      setSubiendo((s) => {
        const copia = { ...s };
        delete copia[el.id];
        return copia;
      });
      elementoASubirRef.current = null;
    }
  };

  // ── Desactivar (PUT activo=false) ───────────────────────────────────────────
  const desactivar = async () => {
    if (!aDesactivar) return;
    setDesactivando(true);
    try {
      await api.put(`/elementos/${aDesactivar.id}`, {
        nombre: aDesactivar.nombre,
        activo: false,
      });
      toast.success(`"${aDesactivar.nombre}" desactivado.`);
      setADesactivar(null);
      cargar();
    } catch {
      toast.error("No se pudo desactivar el elemento.");
    } finally {
      setDesactivando(false);
    }
  };

  const activar = async (el: Elemento) => {
    try {
      await api.put(`/elementos/${el.id}`, { nombre: el.nombre, activo: true });
      toast.success(`"${el.nombre}" activado.`);
      cargar();
    } catch {
      toast.error("No se pudo activar el elemento.");
    }
  };

  // ── Mover a papelera (DELETE → soft delete) ─────────────────────────────────
  const moverAPapelera = async () => {
    if (!aEliminar) return;
    setEliminando(true);
    try {
      await api.delete(`/elementos/${aEliminar.id}`);
      toast.success(`"${aEliminar.nombre}" movido a la papelera. Puedes restaurarlo cuando quieras.`);
      setAEliminar(null);
      cargar();
    } catch (err) {
      if (err instanceof ApiError && err.status === 422) {
        toast.error("Este elemento ya está en la papelera.");
      } else {
        toast.error("No se pudo mover el elemento a la papelera.");
      }
    } finally {
      setEliminando(false);
    }
  };

  // ── Restaurar desde papelera ────────────────────────────────────────────────
  const restaurar = async () => {
    if (!aRestaurar) return;
    setRestaurando(true);
    try {
      await api.post(`/elementos/${aRestaurar.id}/restaurar`, {});
      toast.success(`"${aRestaurar.nombre}" restaurado.`);
      setARestaurar(null);
      cargar();
    } catch (err) {
      if (err instanceof ApiError && err.status === 422) {
        toast.error("Este elemento no está en la papelera.");
      } else {
        toast.error("No se pudo restaurar el elemento.");
      }
    } finally {
      setRestaurando(false);
    }
  };

  // ── Purgar (hard delete, irreversible) ──────────────────────────────────────
  /**
   * Borrado definitivo, confirmado EN LA FILA.
   *
   * El diálogo que pedía tipear "ELIMINAR" se fue: para un elemento que ya está
   * en la papelera, esa fricción no compraba nada —el paso destructivo real fue
   * mandarlo ahí— y el prototipo resuelve la confirmación con dos clicks.
   */
  const purgarDirecto = async (el: Elemento) => {
    setPurgando(true);
    try {
      await api.delete(`/elementos/${el.id}/purgar`);
      toast.success(`"${el.nombre}" eliminado permanentemente.`);
      cargar();
    } catch (err) {
      if (err instanceof ApiError && err.status === 422) {
        toast.error("Solo se puede purgar un elemento desde la papelera.");
      } else {
        toast.error("No se pudo purgar el elemento.");
      }
    } finally {
      setPurgando(false);
      setConfirmandoBorrado(null);
    }
  };

  // ── Conjuntos derivados ─────────────────────────────────────────────────────

  const activos = useMemo(() => inventario.filter((e) => e.activo), [inventario]);
  const inactivos = useMemo(() => inventario.filter((e) => !e.activo), [inventario]);

  /**
   * Las cuatro tarjetas se pliegan del MISMO conjunto (`inventario`) y con la
   * MISMA función que pinta las columnas, así no pueden discrepar.
   */
  const kpis = useMemo(() => {
    const estados = inventario.map(estadoCertificado);
    return {
      total: inventario.length,
      vencidos: estados.filter((c) => c.vencido).length,
      sinSubir: estados.filter((c) => !c.cargado).length,
      alDia: estados.filter((c) => c.alDia).length,
    };
  }, [inventario]);

  const filas = useMemo(() => {
    const base =
      vista === "todos"
        ? inventario
        : vista === "activos"
          ? activos
          : vista === "inactivos"
            ? inactivos
            : papelera;
    const q = search.trim().toLowerCase();
    if (!q) return base;
    // Con la categoría oculta no se busca por ella: encontrar una fila por un
    // dato que no está en pantalla se lee como que el buscador se equivocó.
    return base.filter(
      (e) =>
        e.nombre.toLowerCase().includes(q) ||
        (CATEGORIA_VISIBLE && (e.categoria_etiqueta ?? "").toLowerCase().includes(q)),
    );
  }, [vista, inventario, activos, inactivos, papelera, search]);

  const esPapelera = vista === "papelera";

  // Incluye la PAPELERA a propósito: la regla de unicidad del backend también
  // la mira ("puede estar en la papelera"), así que avisar sólo contra el
  // catálogo vivo dejaría pasar un nombre que el 422 iba a rechazar igual.
  const nombresUsados = useMemo(
    () => [...inventario, ...papelera].map((e) => e.nombre),
    [inventario, papelera],
  );

  const mensajeVacio = search.trim()
    ? "Ningún elemento coincide con esa búsqueda."
    : esPapelera
      ? "La papelera está vacía."
      : vista === "inactivos"
        ? "No hay elementos inactivos."
        : 'Todavía no hay elementos. Haz clic en "Nuevo elemento" para empezar.';

  return (
    <div className="space-y-6">
      <PageHeader
        title="EPP"
        subtitle="Los equipos de protección que entrega tu empresa, con su certificado."
        rightContent={
          <Button
            data-tour="epp-crear"
            onClick={() => setCreateOpen(true)}
            className="shrink-0"
          >
            <Plus className="h-4 w-4" />
            Nuevo elemento
          </Button>
        }
      />
      {/*
        modal={false} desactiva el focus trap de Radix Dialog durante los pasos
        in-dialog de la guía. Esto permite que los botones Siguiente/Anterior de
        driver.js (renderizados fuera del DOM del diálogo) sean clickeables.
        En el flujo normal sin guía, guideInDialog=false y el diálogo es modal.
      */}
      <FormDrawer open={createOpen} onOpenChange={(open) => {
        if (!open) setGuideInDialog(false);
        setCreateOpen(open);
      }} modal={guideInDialog ? false : undefined} dismissible={guideInDialog ? false : undefined}>
        <ElementoFormDrawer
          key={createOpen ? "create-open" : "create-closed"}
          mode="create"
          nombresUsados={nombresUsados}
          onSaved={() => {
            setCreateOpen(false);
            setGuideInDialog(false);
            cargar();
            invalidate();
          }}
        />
      </FormDrawer>

      <KpiGrid min={196}>
        <KpiCard
          label="Elementos en catálogo"
          valor={kpis.total}
          detalle={`${plural(activos.length, "activo", "activos")} · ${plural(
            inactivos.length,
            "inactivo",
            "inactivos",
          )}`}
        />
        <KpiCard
          label="Certificado vencido"
          valor={kpis.vencidos}
          alerta={kpis.vencidos > 0}
          detalle={`de ${plural(kpis.total, "elemento", "elementos")} en el catálogo`}
        />
        <KpiCard
          label="Certificado sin subir"
          valor={kpis.sinSubir}
          detalle="todavía no tienen el certificado de calidad cargado"
        />
        <KpiCard
          label="Con certificado al día"
          valor={`${kpis.alDia}/${kpis.total}`}
          detalle="con certificado cargado y sin vencer"
        />
      </KpiGrid>

      {isLoading ? (
        <Card>
          <CardContent className="flex items-center justify-center py-12 pt-6">
            <Loader2 className="text-muted-foreground h-6 w-6 animate-spin" />
          </CardContent>
        </Card>
      ) : loadError ? (
        <Card>
          <CardContent className="flex flex-col items-center gap-3 py-12 pt-6 text-center">
            <AlertCircle className="text-destructive h-6 w-6" />
            <p className="text-muted-foreground text-sm">{loadError}</p>
            <button type="button" onClick={cargar} className="text-primary-hover text-sm underline">
              Reintentar
            </button>
          </CardContent>
        </Card>
      ) : (
        <DataTable
          rows={filas}
          getRowKey={(el) => el.id}
          // 862px de columnas + 28px del `px-3.5` de la fila. Por debajo de esa
          // suma la grilla se desborda de su contenedor en vez de scrollear, y
          // la cabecera deja de alinearse con las filas.
          minWidth={MIN_WIDTH_TABLA}
          pageSize={20}
          emptyMessage={mensajeVacio}
          emptyIcon={esPapelera ? "delete" : "inventory_2"}
          toolbar={
            <>
              <ToggleGroup
                type="single"
                variant="segmented"
                value={vista}
                onValueChange={(v) => {
                  if (!v) return;
                  setVista(v as Vista);
                  setSearch("");
                }}
              >
                <ToggleGroupItem value="todos">
                  Todos
                  <ToggleGroupCount>{inventario.length}</ToggleGroupCount>
                </ToggleGroupItem>
                <ToggleGroupItem value="activos">
                  Activos
                  <ToggleGroupCount>{activos.length}</ToggleGroupCount>
                </ToggleGroupItem>
                <ToggleGroupItem value="inactivos">
                  Inactivos
                  <ToggleGroupCount>{inactivos.length}</ToggleGroupCount>
                </ToggleGroupItem>
                <ToggleGroupItem value="papelera">
                  <Icon name="delete" size={16} />
                  Papelera
                  <ToggleGroupCount>{papelera.length}</ToggleGroupCount>
                </ToggleGroupItem>
              </ToggleGroup>

              <div className="relative min-w-[190px] flex-1">
                <Search className="text-soft absolute top-1/2 left-2.5 h-4 w-4 -translate-y-1/2" />
                <Input
                  placeholder="Buscar elemento…"
                  className="h-9 pl-8 text-[13px]"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                />
              </div>
            </>
          }
          columns={[
            {
              key: "elemento",
              header: "Elemento",
              width: "minmax(220px,1.6fr)",
              cell: (el) => <CeldaElemento el={el} />,
            },
            // La columna se saca del array, no se esconde con CSS: `DataTable`
            // arma su `grid-template-columns` desde esta lista, así que una
            // columna oculta con `hidden` igual reservaría sus 116px y dejaría
            // un hueco muerto entre Elemento y Certificado.
            ...(CATEGORIA_VISIBLE
              ? [
                  {
                    key: "categoria",
                    header: "Categoría",
                    width: "116px",
                    cell: (el: Elemento) => (
                      <TextoTruncado className="text-muted-foreground block truncate pr-2 text-xs">
                        {el.categoria_etiqueta ?? "Sin categoría"}
                      </TextoTruncado>
                    ),
                  },
                ]
              : []),
            {
              key: "certificado",
              header: "Certificado",
              width: "186px",
              cell: (el) =>
                // En la papelera el elemento salió del catálogo: su certificado
                // no se está exigiendo y no hay nada que subir ni corregir.
                esPapelera ? (
                  <span className="text-soft text-xs">—</span>
                ) : (
                  <CeldaCertificado
                    el={el}
                    subiendo={!!subiendo[el.id]}
                    ocupada={ocupada === el.id}
                    onSubir={pedirArchivo}
                    onDescargar={descargarCertificado}
                  />
                ),
            },
            {
              key: "vencimiento",
              header: "Vencimiento",
              width: "176px",
              cell: (el) =>
                esPapelera ? (
                  <span className="text-soft text-xs">—</span>
                ) : (
                  <CeldaVencimiento
                    el={el}
                    ocupada={ocupada === el.id}
                    onAbrir={abrirVigencia}
                  />
                ),
            },
            {
              key: "estado",
              header: "Estado",
              width: "100px",
              cell: (el) => <EstadoBadge el={el} />,
            },
            {
              // Sin encabezado, igual que el prototipo. Cadena vacía y NO
              // `srOnlyHeader`: `sr-only` es `position:absolute`, así que sobre
              // una celda de la grilla la saca del flujo y corre las columnas
              // siguientes — ver el comentario en DataTable.
              key: "acciones",
              header: "",
              width: "150px",
              align: "right",
              cell: (el) =>
                esPapelera ? (
                  <div className="flex items-center justify-end gap-1.5">
                    {/* Confirmación de DOS PASOS en la fila, como el prototipo:
                        el primer click arma el botón rojo y el segundo borra.
                        Reemplaza al diálogo que pedía tipear "ELIMINAR" — para
                        un elemento de catálogo que ya está en la papelera, esa
                        fricción no compraba nada. */}
                    {confirmandoBorrado === el.id && (
                      <Button
                        size="xs"
                        variant="outline"
                        disabled={purgando}
                        className="border-destructive-bar/40 bg-destructive-bg text-destructive hover:bg-destructive-bg hover:border-destructive-bar"
                        onClick={() => purgarDirecto(el)}
                      >
                        {purgando ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : "Sí, borrar"}
                      </Button>
                    )}
                    <Tip texto="Devolver al catálogo">
                      <Button
                        variant="outline"
                        size="icon-sm"
                        className="border-primary-border bg-primary-50 text-primary-hover hover:bg-primary-100"
                        aria-label={`Restaurar ${el.nombre}`}
                        onClick={() => setARestaurar(el)}
                      >
                        <Icon name="restore_from_trash" size={16} />
                      </Button>
                    </Tip>
                    <Tip texto="Eliminar definitivamente">
                      <Button
                        variant="ghost"
                        size="icon-sm"
                        className="text-soft hover:text-destructive"
                        aria-label={`Eliminar definitivamente ${el.nombre}`}
                        onClick={() =>
                          setConfirmandoBorrado(confirmandoBorrado === el.id ? null : el.id)
                        }
                      >
                        <Icon name="delete_forever" size={16} />
                      </Button>
                    </Tip>
                  </div>
                ) : (
                  // A DIFERENCIA de Vehículos y Maquinaria, acá editar y
                  // activar/desactivar SE QUEDAN en la fila. Aquellos las
                  // mudaron a la ficha del recurso; un elemento de EPP no tiene
                  // ficha, así que sacarlas de la fila dejaría el catálogo sin
                  // forma de renombrar ni de dar de baja un elemento.
                  <div className="flex items-center justify-end gap-1.5">
                    {/* SWITCH, no un icono de ojo. El estado activo/inactivo es
                        algo que se prende y se apaga y que hay que poder LEER
                        de un vistazo en la fila; un ojo obliga a interpretar si
                        muestra el estado actual o la acción que hace. */}
                    <Tip
                      texto={
                        el.activo
                          ? "Desactivar: deja de ofrecerse en las entregas de EPP"
                          : "Activar: vuelve a ofrecerse en las entregas"
                      }
                    >
                      <span className="flex items-center">
                        <Switch
                          checked={el.activo}
                          aria-label={`${el.activo ? "Desactivar" : "Activar"} ${el.nombre}`}
                          onCheckedChange={() => (el.activo ? setADesactivar(el) : activar(el))}
                        />
                      </span>
                    </Tip>
                    <Tip texto="Editar elemento">
                      <Button
                        variant="ghost"
                        size="icon-sm"
                        aria-label={`Editar ${el.nombre}`}
                        onClick={() => setEditing(el)}
                      >
                        <Icon name="edit" size={16} />
                      </Button>
                    </Tip>
                    <Tip texto="Mover a la papelera — se puede restaurar">
                      <Button
                        variant="ghost"
                        size="icon-sm"
                        className="text-soft hover:text-destructive"
                        aria-label={`Mover ${el.nombre} a la papelera`}
                        onClick={() => setAEliminar(el)}
                      >
                        <Icon name="delete" size={16} />
                      </Button>
                    </Tip>
                  </div>
                ),
            },
          ]}
        />
      )}


      {/* Dialog edición */}
      <FormDrawer open={editing !== null} onOpenChange={(open) => !open && setEditing(null)}>
        {editing && (
          <ElementoFormDrawer
            key={`edit-${editing.id}`}
            mode="edit"
            elemento={editing}
            nombresUsados={nombresUsados}
            onSaved={() => {
              setEditing(null);
              cargar();
            }}
          />
        )}
      </FormDrawer>


      {/* Confirmar: desactivar (PUT activo=false) */}
      <AlertDialog
        open={aDesactivar !== null}
        onOpenChange={(open) => !open && setADesactivar(null)}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Desactivar elemento</AlertDialogTitle>
            <AlertDialogDescription>
              {aDesactivar ? (
                <>
                  ¿Seguro que quieres desactivar <strong>{aDesactivar.nombre}</strong>? Va a dejar de
                  aparecer al armar nuevas entregas. Las entregas históricas no se ven afectadas y
                  puedes reactivarlo cuando quieras.
                  <span className="mt-1 block text-xs">
                    Nota: desactivar <em>no</em> elimina el elemento — para eso usa &ldquo;Mover a papelera&rdquo;.
                  </span>
                </>
              ) : null}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={desactivando}>Cancelar</AlertDialogCancel>
            <AlertDialogAction
              onClick={(e) => {
                e.preventDefault();
                desactivar();
              }}
              disabled={desactivando}
            >
              {desactivando ? <Loader2 className="h-4 w-4 animate-spin" /> : "Desactivar"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Confirmar: mover a papelera */}
      <AlertDialog
        open={aEliminar !== null}
        onOpenChange={(open) => !open && setAEliminar(null)}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Mover a papelera</AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div>
                {aEliminar ? (
                  <div className="space-y-2">
                    <p>
                      ¿Quieres mover <strong>{aEliminar.nombre}</strong> a la papelera?
                    </p>
                    <p>
                      Va a desaparecer del catálogo activo pero puedes{" "}
                      <strong>restaurarlo en cualquier momento</strong> desde la sección Papelera.
                    </p>
                    <p className="text-xs text-muted-foreground">
                      Distinto de <em>Desactivar</em>: mover a papelera quita el elemento del catálogo.
                      Desactivar solo lo oculta al armar nuevas entregas.
                    </p>
                  </div>
                ) : null}
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={eliminando}>Cancelar</AlertDialogCancel>
            <AlertDialogAction
              onClick={(e) => {
                e.preventDefault();
                moverAPapelera();
              }}
              disabled={eliminando}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {eliminando ? <Loader2 className="h-4 w-4 animate-spin" /> : "Mover a papelera"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Confirmar: restaurar */}
      <AlertDialog
        open={aRestaurar !== null}
        onOpenChange={(open) => !open && setARestaurar(null)}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Restaurar elemento</AlertDialogTitle>
            <AlertDialogDescription>
              {aRestaurar ? (
                <>
                  ¿Restaurar <strong>{aRestaurar.nombre}</strong>? Vuelve al catálogo activo con el
                  estado que tenía antes de ser eliminado.
                </>
              ) : null}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={restaurando}>Cancelar</AlertDialogCancel>
            <AlertDialogAction
              onClick={(e) => {
                e.preventDefault();
                restaurar();
              }}
              disabled={restaurando}
            >
              {restaurando ? <Loader2 className="h-4 w-4 animate-spin" /> : "Restaurar"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>


      {/* Vigencia del certificado — el MISMO componente del DS que usan la
          ficha del trabajador y los documentos de empresa. Antes había que
          entrar al diálogo del certificado para llegar acá; ahora la celda de
          Vencimiento lo abre directo, que es lo que pide el prototipo. */}
      <VigenciaModal
        open={vigencia !== null}
        onOpenChange={(open) => {
          if (!open) setVigencia(null);
        }}
        label={
          vigencia ? `Certificado de calidad — ${vigencia.elemento.nombre}` : "Certificado de calidad"
        }
        documento={vigencia?.documento ?? null}
        onSubmit={async ({ fechaVencimiento, notificarDias }) => {
          if (!vigencia) return;
          await adapterDocs.updateVencimiento("elemento", vigencia.elemento.id, vigencia.documento.id, {
            fechaVencimiento,
            notificarDias,
          });
          setVigencia(null);
          cargar();
        }}
      />

      {/* Un solo input para toda la tabla. `key` con el id fuerza a React a
          recrearlo, así subir DOS VECES el mismo archivo vuelve a disparar
          `onChange` (que no salta si el value no cambió). */}
      <input
        ref={inputArchivoRef}
        type="file"
        accept="application/pdf,image/*"
        className="hidden"
        onChange={(e) => {
          const archivo = e.target.files?.[0];
          e.target.value = "";
          if (archivo) subirCertificado(archivo);
        }}
      />
    </div>
  );
}

interface ElementoFormDrawerProps {
  mode: "create" | "edit";
  elemento?: Elemento;
  /** Nombres ya usados en el catálogo — para avisar del duplicado ANTES de
   *  mandar y para no sugerir algo que la empresa ya tiene. */
  nombresUsados: string[];
  onSaved: () => void;
}

function ElementoFormDrawer({ mode, elemento, nombresUsados, onSaved }: ElementoFormDrawerProps) {
  const editar = mode === "edit";
  const [nombre, setNombre] = useState(elemento?.nombre ?? "");
  const [activo, setActivo] = useState<boolean>(elemento?.activo ?? true);
  const [categoria, setCategoria] = useState<CategoriaElemento | "">(elemento?.categoria ?? "");
  const [isSubmitting, setIsSubmitting] = useState(false);

  const limpio = nombre.trim();

  // Los nombres del propio elemento no cuentan como duplicado de sí mismo.
  const ocupados = useMemo(() => {
    const otros = nombresUsados.filter((n) => !editar || n !== elemento?.nombre);
    return new Set(otros.map((n) => n.toLowerCase()));
  }, [nombresUsados, editar, elemento?.nombre]);

  // Se avisa MIENTRAS se escribe y no al guardar. El backend igual lo rechaza
  // con un 422 —es su regla, no la nuestra—, pero enterarse recién ahí obliga a
  // reescribir el nombre después de haber apretado Guardar.
  const duplicado = limpio.length > 0 && ocupados.has(limpio.toLowerCase());

  const huboCambio =
    !editar ||
    limpio !== elemento?.nombre ||
    activo !== elemento?.activo ||
    (categoria || null) !== (elemento?.categoria ?? null);

  const puedeGuardar = limpio.length > 0 && !duplicado && huboCambio;

  /** Una sola línea en el pie que dice qué falta o qué va a pasar. */
  const hint = duplicado
    ? "Cambia el nombre: ese elemento ya está en el catálogo."
    : !limpio
      ? "Escribe un nombre para continuar."
      : editar
        ? huboCambio
          ? "El certificado y su vencimiento no cambian."
          : "No has hecho cambios."
        : "Su certificado se sube después, desde la tabla.";

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!puedeGuardar || isSubmitting) return;

    // Obligatoria acá y no en la API: el request la acepta opcional para no
    // romper los PUT parciales de activar/desactivar. Ver ElementoRequest.
    // Y sólo se exige cuando el campo se ve — si no, el formulario quedaría
    // imposible de enviar pidiendo algo que nadie puede completar.
    if (CATEGORIA_VISIBLE && !categoria) {
      toast.error("Elige la categoría del elemento.");
      return;
    }

    const payload = {
      nombre: limpio,
      activo,
      // `renovacion_meses` NO viaja: el campo se fue del formulario y la regla
      // del request es `sometimes`, así que omitirlo CONSERVA lo que el
      // elemento ya tenía. Mandarlo en `null` le borraría el ciclo al catálogo
      // sembrado, que es de donde sale la línea "renueva cada 2 años".
      // La categoría sólo viaja si hay una. Importa con el campo oculto: al
      // editar, `categoria` viene precargada del elemento y así CONSERVA la que
      // ya tenía; mandar "" la haría rebotar contra el enum y mandar `null` se
      // la borraría a un elemento que sí estaba clasificado.
      ...(categoria ? { categoria } : {}),
    };
    setIsSubmitting(true);
    try {
      if (editar && elemento) {
        await api.put(`/elementos/${elemento.id}`, payload);
        toast.success(
          limpio !== elemento.nombre
            ? `"${elemento.nombre}" ahora se llama "${limpio}".`
            : `Cambios guardados en "${limpio}".`,
        );
      } else {
        await api.post("/elementos", payload);
        toast.success(`"${limpio}" agregado al catálogo.`);
      }
      onSaved();
    } catch (err) {
      if (err instanceof ApiError && err.status === 422 && err.errors) {
        const firstError = Object.values(err.errors)[0]?.[0];
        toast.error(firstError ?? "Revisa los datos.");
      } else {
        toast.error("No se pudo guardar el elemento.");
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <FormDrawerContent>
      <FormDrawerHeader>
        <div className="flex items-center gap-3">
          <span className="bg-primary-50 text-primary-hover flex size-9 shrink-0 items-center justify-center rounded-[10px]">
            <Icon name="health_and_safety" size={20} />
          </span>
          <div className="min-w-0 flex-1">
            <FormDrawerTitle>
              {editar ? "Editar elemento" : "Nuevo elemento de EPP"}
            </FormDrawerTitle>
            <FormDrawerDescription>
              {editar
                ? "Los cambios se reflejan en las entregas y checklists futuros."
                : "Queda disponible para las entregas y los checklists de terreno."}
            </FormDrawerDescription>
          </div>
        </div>
      </FormDrawerHeader>

      <form onSubmit={submit} className={formDrawerBodyClass}>
        <div className="space-y-2" data-tour="epp-nombre">
          <Label htmlFor="nombre">Nombre del elemento</Label>
          <Input
            id="nombre"
            value={nombre}
            onChange={(e) => setNombre(e.target.value)}
            placeholder="Ej: Casco con barbiquejo, Guantes dieléctricos clase 0"
            className={cn(duplicado && "border-warning-bar focus-visible:ring-warning-bar/30")}
            disabled={isSubmitting}
            autoFocus
            required
          />
          {duplicado && (
            <p className="text-warning flex items-center gap-1.5 text-xs">
              <Icon name="error_outline" size={16} className="shrink-0" />
              Ya existe un elemento con ese nombre en el catálogo.
            </p>
          )}
        </div>

        {CATEGORIA_VISIBLE && (
          <div className="space-y-2">
            <Label htmlFor="categoria">Categoría</Label>
            <NativeSelect
              id="categoria"
              value={categoria}
              onChange={(e) => setCategoria(e.target.value as CategoriaElemento | "")}
              disabled={isSubmitting}
            >
              <NativeSelectOption value="">Elige una categoría</NativeSelectOption>
              {CATEGORIAS_ELEMENTO.map((c) => (
                <NativeSelectOption key={c.valor} value={c.valor}>
                  {c.etiqueta}
                </NativeSelectOption>
              ))}
            </NativeSelect>
          </div>
        )}


        {/* El copy cambia con el estado en vez de ser una etiqueta fija
            "Activo": así la frase dice lo que ESTÁ pasando, no el nombre del
            campo. */}
        <div className="flex items-center justify-between gap-3 rounded-lg border p-3">
          <div className="min-w-0 space-y-0.5">
            <Label htmlFor="activo" className="block text-[13px] font-semibold">
              {activo
                ? "Activo en el catálogo"
                : editar
                  ? "Inactivo"
                  : "Se crea inactivo"}
            </Label>
            <p className="text-muted-foreground text-xs leading-relaxed text-pretty">
              {activo
                ? "Se puede asignar en entregas de EPP y aparece en los checklists de terreno."
                : "Queda guardado pero no se ofrece en entregas ni checklists hasta que lo actives."}
            </p>
          </div>
          <Switch
            id="activo"
            checked={activo}
            onCheckedChange={setActivo}
            disabled={isSubmitting}
          />
        </div>

        <FormDrawerFooter>
          <span className="text-meta mr-auto text-xs leading-snug">{hint}</span>
          <Button
            type="submit"
            disabled={isSubmitting || !puedeGuardar}
            className="gap-2"
            data-tour="epp-guardar"
          >
            {isSubmitting ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <Icon name={editar ? "check" : "add"} size={16} />
            )}
            {editar ? "Guardar cambios" : "Agregar al catálogo"}
          </Button>
        </FormDrawerFooter>
      </form>
    </FormDrawerContent>
  );
}
